import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { CookieSettings, CookieSettingsService } from './cookie-settings.service';
import { normalizeRole } from '../../shared/utils/role.utils';

export interface CookieConsentSelection {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  readonly settings = signal<CookieSettings | null>(null);
  readonly consent = signal<CookieConsentSelection | null>(null);
  readonly bannerVisible = signal(false);
  readonly preferencesVisible = signal(false);
  readonly initialized = signal(false);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieSettingsService = inject(CookieSettingsService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized()) {
      return;
    }

    this.cookieSettingsService.getPublicSettings().subscribe({
      next: (settings) => {
        this.settings.set(settings);
        this.initialized.set(true);
        this.evaluateBannerState();
      },
      error: (error) => {
        console.error('Failed to load cookie settings', error);
        this.initialized.set(true);
        this.bannerVisible.set(false);
      }
    });

    this.authService.currentUser$.subscribe(() => {
      this.evaluateBannerState();
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.evaluateBannerState();
      });
  }

  openPreferences(): void {
    this.preferencesVisible.set(true);
  }

  closePreferences(): void {
    this.preferencesVisible.set(false);
  }

  acceptAll(): void {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    this.persistConsent({
      analytics: settings.enableAnalytics,
      marketing: settings.enableMarketing,
      preferences: settings.enablePreferences
    });
  }

  rejectNonEssential(): void {
    this.persistConsent({
      analytics: false,
      marketing: false,
      preferences: false
    });
  }

  saveCustom(selection: CookieConsentSelection): void {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    this.persistConsent({
      analytics: settings.enableAnalytics ? selection.analytics : false,
      marketing: settings.enableMarketing ? selection.marketing : false,
      preferences: settings.enablePreferences ? selection.preferences : false
    });
  }

  isCategoryEnabled(category: keyof CookieConsentSelection): boolean {
    const settings = this.settings();
    if (!settings) {
      return false;
    }

    const keyMap: Record<keyof CookieConsentSelection, keyof CookieSettings> = {
      analytics: 'enableAnalytics',
      marketing: 'enableMarketing',
      preferences: 'enablePreferences'
    };

    return Boolean(settings[keyMap[category]]);
  }

  private evaluateBannerState(): void {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    if (!this.shouldConsiderBanner()) {
      this.consent.set(null);
      this.bannerVisible.set(false);
      this.preferencesVisible.set(false);
      return;
    }

    if (!settings.enableBanner) {
      this.bannerVisible.set(false);
      return;
    }

    this.cookieSettingsService.getMyConsent().subscribe({
      next: (storedConsent) => {
        if (!storedConsent) {
          this.consent.set(null);
          this.bannerVisible.set(true);
          return;
        }

        const normalizedConsent: CookieConsentSelection = {
          analytics: settings.enableAnalytics ? storedConsent.analytics : false,
          marketing: settings.enableMarketing ? storedConsent.marketing : false,
          preferences: settings.enablePreferences ? storedConsent.preferences : false
        };

        this.consent.set(normalizedConsent);
        this.bannerVisible.set(false);
        this.broadcastConsent(normalizedConsent);
      },
      error: (error) => {
        console.error('Failed to load current user cookie consent', error);
        this.consent.set(null);
        this.bannerVisible.set(true);
      }
    });
  }

  private persistConsent(selection: CookieConsentSelection): void {
    if (!this.shouldConsiderBanner()) {
      return;
    }

    this.consent.set(selection);
    this.bannerVisible.set(false);
    this.preferencesVisible.set(false);
    this.broadcastConsent(selection);

    this.cookieSettingsService
      .saveConsent(selection)
      .subscribe({
        error: (error) => {
          console.error('Failed to persist cookie consent in backend', error);
          this.bannerVisible.set(true);
        }
      });
  }

  private broadcastConsent(selection: CookieConsentSelection): void {
    window.dispatchEvent(
      new CustomEvent('rma-cookie-consent-updated', {
        detail: selection
      })
    );
  }

  private shouldConsiderBanner(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !this.authService.getToken()) {
      return false;
    }

    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    if (currentUrl === '/login') {
      return false;
    }

    const normalizedRole = normalizeRole(currentUser.role || currentUser.roleName || '');
    const isDelegatedPropertyManager = !!(currentUser.assignedLandlordId || currentUser.assignedLandlord);

    if (normalizedRole === 'TENANT') {
      return true;
    }

    if (normalizedRole === 'LANDLORD') {
      return true;
    }

    if (isDelegatedPropertyManager) {
      return true;
    }

    return false;
  }
}
